import type { Chat, KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import {
  ROOT_FOLDER_ID,
  type FileNode,
  type FileSystemNode,
  type FolderCrumb,
  type FolderNode,
} from '../../types/fileSystem.js';
import { getNodeName, toFileNode, toFolderNode } from './fileSystemNode.js';

export type PrivateBucket = FolderNode & {
  ownerUuid: string | null;
};

type FileSystemListing = {
  folders: FolderNode[];
  files: FileNode[];
  privateBuckets: PrivateBucket[];
  breadcrumb: FolderCrumb[];
};

/** Shared so a missing listing keeps returning the same object. */
const EMPTY: FileSystemListing = {
  folders: [],
  files: [],
  privateBuckets: [],
  breadcrumb: [],
};

const matches = (node: FileSystemNode, query: string) =>
  getNodeName(node).toLowerCase().includes(query);

export const getPrivateBucketName = (folder: KnowledgeBase.Folder) => {
  const ownerName = [folder.owner?.firstName, folder.owner?.lastName]
    .filter(Boolean)
    .join(' ');

  return ownerName ? `${ownerName}’s private folder` : folder.name;
};

const toPrivateBucket = (
  folder: KnowledgeBase.Folder,
  folderId: string,
): PrivateBucket => ({
  ...toFolderNode(folder, folderId),
  name: getPrivateBucketName(folder),
  // `owner` is filled in for private buckets; `createdBy` covers the ones the
  // server has not enriched.
  ownerUuid: folder.owner?.userUuid ?? folder.createdBy ?? null,
});

/**
 * Turns one listing into what the screen renders. Private buckets are pulled
 * aside for their own cards and never reach the table.
 */
export const readListing = (
  listing: KnowledgeBase.KnowledgeBaseListing | undefined,
  folderId: string,
  query: string,
): FileSystemListing => {
  if (!listing) return EMPTY;

  const folders = listing.folders ?? [];
  const isPrivateBucket = (folder: KnowledgeBase.Folder) =>
    folderId === ROOT_FOLDER_ID && Boolean(folder.isPrivate);

  return {
    privateBuckets: folders
      .filter(isPrivateBucket)
      .map((folder) => toPrivateBucket(folder, folderId)),

    folders: folders
      .filter((folder) => !isPrivateBucket(folder))
      .map((folder) => toFolderNode(folder, folderId))
      .filter((node) => matches(node, query)),

    files: (listing.files ?? [])
      .map((file) => toFileNode(file))
      .filter((node) => matches(node, query)),

    // The server may or may not name the root, so unnamed crumbs are dropped
    // and the caller prepends its own.
    breadcrumb: (listing.breadcrumb ?? []).filter(
      (crumb): crumb is FolderCrumb => Boolean(crumb.folderId),
    ),
  };
};

// aiFileSystem cleanup: delete with the flat documents source.
export const readDocuments = (
  documents: Chat.RagDocumentType[],
  query: string,
): FileSystemListing => ({
  ...EMPTY,
  files: documents
    .map((document) => toFileNode(document))
    .filter((node) => matches(node, query)),
});
